import type { BaseDictionaryItem } from './dictionaryUtils'

/** Type for appointment status dictionary items. */
export type AppointmentStatusItem = BaseDictionaryItem

/** Type for appointment method dictionary items. */
export type AppointmentMethodItem = BaseDictionaryItem

/** Type for appointment outcome dictionary items. */
export type AppointmentOutcomeItem = BaseDictionaryItem

/** Type for appointment decision dictionary items. */
export type AppointmentDecisionItem = BaseDictionaryItem & {
  applicableOutcomeTypes?: readonly string[]
  applicableContractTypes?: readonly string[]
}

export const APPOINTMENT_STATUS: readonly AppointmentStatusItem[] = [
  {
    key: 'unassigned',
    label: 'Unassigned',
    localeKey: 'ui.entities.appointment.status.unassigned',
    icon: 'user-slash',
    color: 'warning'
  },
  {
    key: 'assigned',
    label: 'Assigned',
    localeKey: 'ui.entities.appointment.status.assigned',
    icon: 'user-check',
    color: 'info'
  },
  {
    key: 'cancelled',
    label: 'Cancelled',
    localeKey: 'ui.entities.appointment.status.cancelled',
    icon: 'ban',
    color: 'error'
  },
  {
    key: 'no-show',
    label: 'No Show',
    localeKey: 'ui.entities.appointment.status.noShow',
    icon: 'user-xmark',
    color: 'error'
  },
  {
    key: 'completed',
    label: 'Completed',
    localeKey: 'ui.entities.appointment.status.completed',
    icon: 'check',
    color: 'success'
  },
  {
    key: 'ongoing',
    label: 'Ongoing',
    localeKey: 'ui.entities.appointment.status.ongoing',
    icon: 'clock',
    color: 'brand'
  },
  { key: 'ERROR', label: 'ERROR', localeKey: 'dev.dataMappingError', icon: 'bug', color: 'error' }
] as const

export const APPOINTMENT_METHOD: readonly AppointmentMethodItem[] = [
  {
    key: 'physical',
    label: 'Physical',
    localeKey: 'ui.entities.appointment.method.physical',
    icon: 'car-building'
  },
  {
    key: 'phone',
    label: 'Phone',
    localeKey: 'ui.entities.appointment.method.phone',
    icon: 'phone'
  },
  {
    key: 'video',
    label: 'Video',
    localeKey: 'ui.entities.appointment.method.video',
    icon: 'video'
  },
  {
    key: 'video_external',
    label: 'Video External',
    localeKey: 'ui.entities.appointment.method.video',
    icon: 'video'
  },
  { key: 'ERROR', label: 'ERROR', localeKey: 'dev.dataMappingError', icon: 'bug', color: 'error' }
] as const

export const APPOINTMENT_OUTCOME: readonly AppointmentOutcomeItem[] = [
  {
    key: 'no-show',
    label: 'No Show',
    localeKey: 'ui.entities.appointment.status.noShow',
    color: 'error',
    icon: 'ban'
  },
  {
    key: 'completed',
    label: 'Completed',
    localeKey: 'ui.entities.appointment.status.completed',
    color: 'success',
    icon: 'check'
  },
  {
    key: 'ongoing',
    label: 'Ongoing',
    localeKey: 'ui.entities.appointment.status.ongoing',
    color: 'info',
    icon: 'arrows-rotate'
  },
  { key: 'ERROR', label: 'ERROR', localeKey: 'dev.dataMappingError', icon: 'bug', color: 'error' }
] as const

export const APPOINTMENT_DECISION: readonly AppointmentDecisionItem[] = [
  {
    key: 'part_exchanged',
    label: 'Part-Exchanged',
    localeKey: 'ui.entities.appointment.decision.partExchanged',
    applicableOutcomeTypes: ['completed'],
    applicableContractTypes: ['PCP', 'HP']
  },
  {
    key: 'refinanced',
    label: 'Refinanced',
    localeKey: 'ui.entities.appointment.decision.refinanced',
    applicableOutcomeTypes: ['completed'],
    applicableContractTypes: ['PCP']
  },
  {
    key: 'settled',
    label: 'Settled',
    localeKey: 'ui.entities.appointment.decision.settled',
    applicableOutcomeTypes: ['completed'],
    applicableContractTypes: ['PCP']
  },
  {
    key: 'handed_back',
    label: 'Handed Back',
    localeKey: 'ui.entities.appointment.decision.handedBack',
    applicableOutcomeTypes: ['completed'],
    applicableContractTypes: ['PCP', 'HP', 'CH', 'PCH', 'BCH']
  },
  {
    key: 'buy_back',
    label: 'Buy Back',
    localeKey: 'ui.entities.appointment.decision.buyBack',
    applicableOutcomeTypes: ['completed'],
    applicableContractTypes: ['PCP']
  },
  {
    key: 'new_lease',
    label: 'New Lease',
    localeKey: 'ui.entities.appointment.decision.newLease',
    applicableOutcomeTypes: ['completed'],
    applicableContractTypes: ['CH', 'PCH', 'BCH']
  },
  {
    key: 'extended',
    label: 'Extended',
    localeKey: 'ui.entities.appointment.decision.extended',
    applicableOutcomeTypes: ['completed'],
    applicableContractTypes: ['CH', 'PCH', 'BCH']
  },
  {
    key: 'keeping',
    label: 'Keeping',
    localeKey: 'ui.entities.appointment.decision.keeping',
    applicableOutcomeTypes: ['completed'],
    applicableContractTypes: ['HP']
  },
  {
    key: 'undecided',
    label: 'Undecided',
    localeKey: 'ui.entities.appointment.decision.undecided',
    applicableOutcomeTypes: ['ongoing'],
    applicableContractTypes: ['PCP', 'HP', 'CH', 'PCH', 'BCH']
  },
  {
    key: 'not_ready',
    label: 'Not Ready',
    localeKey: 'ui.entities.appointment.decision.notReady',
    applicableOutcomeTypes: ['ongoing'],
    applicableContractTypes: ['PCP', 'HP', 'CH', 'PCH', 'BCH']
  },
  {
    key: 'poor_offer',
    label: 'Poor Offer',
    localeKey: 'ui.entities.appointment.decision.poorOffer',
    applicableOutcomeTypes: ['ongoing'],
    applicableContractTypes: ['PCP', 'HP', 'CH', 'PCH', 'BCH']
  },
  { key: 'ERROR', localeKey: 'dev.dataMappingError', icon: 'bug', color: 'error' }
] as const
